import { KeyedMap } from '@rivethealth/core/map';
import {
  flatMap,
  get as lodashGet,
  groupBy,
  isEqual,
  isEqual as lodashIsEqual,
  partition as lodashPartition,
  sortBy,
} from 'lodash';
import {
  AuthRequired,
  BenefitDescription,
  BenefitQualifierInfo,
  BenefitTimePeriod,
  Coinsurance,
  Copay,
  Coverage,
  CoverageLevel,
  Deductible,
  EligibilityBenefit,
  EligibilityData,
  EligibilityMessage,
  InNetworkIndicator,
  InterpretedEligibilityResponse,
  Limitation,
  OutOfPocket,
  ServiceType,
} from './eligibility';
import {
  BenefitNetwork,
  CumulativeBenefits,
  EstimationBenefit,
  EstimationTreatment,
  NetworkStatus,
  PatientPayerId,
  patientPayerIds,
} from './estimation';
import { FacilityType } from './location';
import { BenefitType } from './npi';

export const DEFAULT_SERVICE_TYPE: ServiceType = {
  code: '30',
  name: 'Health Benefit Plan Coverage',
  description:
    "General healthcare benefits for the member's policy or contract",
  ref: 'health_benefit_plan_coverage',
};

/**
 * Denormalized benefit info.
 */
interface BenefitPiece {
  network: BenefitNetwork;
  detail: {
    amount: BenefitAmount | null;
    period: BenefitPeriod;
  };
  level: string | null;
  service: string | null;
  note: string | null;
  insuranceType: string | null;
  placeOfService: PlaceOfServiceCode | null;
  authRequired?: AuthRequired;
  benefitQualifier?: BenefitQualifierInfo;
  timePeriod?: BenefitTimePeriod;
  planDescription?: string;
}

export type BenefitPeriod =
  | 'amount'
  | 'lifetime_remaining'
  | 'lifetime'
  | 'year'
  | 'year_to_date'
  | 'remaining'
  | 'visit';

/**
 * Grouped benefit measurements, for certain parameters.
 */
export interface BenefitRecord {
  network: BenefitNetwork;
  details: { [K in BenefitPeriod]?: BenefitAmount };
  level: string | null;
  service: string | null;
  note: string | null;
  insuranceType: string | null;
  placeOfService: PlaceOfServiceCode | null;
  authRequired?: AuthRequired;
  benefitQualifier?: BenefitQualifierInfo;
}

export type GroupedBenefits = {
  periods: BenefitPeriod[];
  benefits: BenefitRecord[];
};

export const emptyGroupBenefits: GroupedBenefits = {
  periods: [],
  benefits: [],
};

export type BenefitAmount =
  | {
      type: 'currency';
      amount: string;
      currency: string;
    }
  | {
      type: 'days';
      amount: number;
    }
  | {
      type: 'visits';
      amount: number;
    }
  | {
      type: 'percent';
      amount: number;
    };

export type BenefitItem =
  | Deductible
  | Coinsurance
  | Copay
  | OutOfPocket
  | Limitation;

export type BenefitItemType =
  | 'deductible'
  | 'copay'
  | 'coinsurance'
  | 'oop'
  | 'limitation';

export enum MedicareABenefitChangeType {
  DEDUCTIBLE_AMOUNT = 'deductible_amount',
  COINSURANCE_AMOUNT = 'coinsurance_amount',
  RESERVE_AMOUNT = 'reserve_amount',
  RESERVE_DAYS_USED = 'reserve_days_used',
}

export interface BenefitRecords {
  benefits: BenefitRecord[];
  periods: BenefitPeriod[];
}

export interface LimitationRecords {
  readableLimitations: BenefitRecord[];
  otherLimitations: EstimationBenefit.Limitation[];
}

export function emptyCoinsurance(): EstimationBenefit.Coinsurance {
  return {
    eligibilityBenefit: null,
    proportion: null,
    custom: false,
  };
}

export function emptyCopay(): EstimationBenefit.Copay {
  return {
    amount: null,
    eligibilityBenefit: null,
    custom: false,
  };
}

export function emptyDeductible(): EstimationBenefit.Deductible {
  return {
    amount: null,
    eligibilityBenefit: null,
    total: null,
    custom: false,
    cascadingBenefit: false,
  };
}

export function emptyOop(): EstimationBenefit.Oop {
  return {
    amount: null,
    eligibilityBenefit: null,
    total: null,
    custom: false,
    cascadingBenefit: false,
  };
}

export function emptyLimitation(): EstimationBenefit.Limitation {
  return {
    eligibilityBenefit: null,
    custom: false,
  };
}

export function emptyBenefit(): EstimationBenefit {
  return {
    coinsurance: emptyCoinsurance(),
    copay: emptyCopay(),
    deductible: emptyDeductible(),
    oop: emptyOop(),
    limitation: emptyLimitation(),
  };
}

export function emptyPayerEstimationBenefit(): EstimationBenefit {
  return {
    coinsurance: emptyCoinsurance(),
    copay: emptyCopay(),
    deductible: {
      ...emptyDeductible(),
      cascadingBenefit: true,
    },
    oop: {
      ...emptyOop(),
      cascadingBenefit: true,
    },
    limitation: emptyLimitation(),
  };
}

function hcaBenefit(serviceTypeCode: string): BenefitRecord {
  return {
    details: {
      amount: {
        type: 'currency',
        amount: '0',
        currency: 'USD',
      },
      remaining: {
        type: 'currency',
        amount: '0',
        currency: 'USD',
      },
    },
    insuranceType: null,
    level: null,
    network: 'not_applicable',
    note: 'HCA',
    placeOfService: PlaceOfServiceCode.NO_PLACE_OF_SERVICE,
    service: serviceTypeCode,
  };
}

// Converts from either a time_period or time_period_qualifier into a Benefit Period.
function convertToBenefitPeriod(benefit: BenefitItem): BenefitPeriod {
  const rawPeriod =
    'time_period' in benefit
      ? benefit.time_period
      : 'time_period_qualifier' in benefit
        ? benefit.time_period_qualifier
        : undefined;

  switch (rawPeriod) {
    case 'calendar_year':
    case 'service_year':
    case 'contract':
    case 'year':
      return 'year';
    case 'lifetime':
    case 'lifetime_remaining':
    case 'remaining':
    case 'visit':
    case 'year_to_date':
      return rawPeriod;
    case 'day':
    case 'episode':
    case 'admission':
    case 'week':
    case 'month':
    case 'twentyfour_hours':
    case 'exceeded':
    case 'not_exceeded':
    case 'no_recognized_time_period':
    case 'amount':
    case undefined:
      return 'amount';
  }

  console.warn(`Could not determine ${rawPeriod} for benefit: %O`, benefit); // eslint-disable-line no-console
  return 'amount';
}

// Figures out the actual numbers behind the Benefit, whether that is an amount of money, or a number of visits to the doctor.
function convertToBenefitAmount(benefit: BenefitItem): BenefitAmount | null {
  if ('benefit_percent' in benefit) {
    return {
      type: 'percent',
      amount: properPercent(benefit.benefit_percent),
    };
  } else if ('copayment' in benefit) {
    return {
      type: 'currency',
      amount: benefit.copayment.amount,
      currency: benefit.copayment.currency,
    };
  } else if ('benefit_amount' in benefit) {
    return {
      type: 'currency',
      amount: benefit.benefit_amount.amount,
      currency: benefit.benefit_amount.currency,
    };
  } else if ('quantity' in benefit) {
    switch (benefit.quantity_qualifier) {
      case 'days':
        return {
          type: 'days',
          amount: benefit.quantity,
        };
      case 'visits':
        return {
          type: 'visits',
          amount: benefit.quantity,
        };
    }
  }
  return null;
}

// Converts from the pokitdok YES or NO values for network to our IN or OUT values.
function convertToBenefitNetwork(benefit: BenefitItem): BenefitNetwork {
  switch (benefit.in_plan_network) {
    case 'yes':
      return 'in';
    case 'no':
      return 'out';
    case 'not_applicable':
      return 'not_applicable';
    case 'unknown':
      return 'not_applicable';
  }
}

// Changes time period on benefit based on plan description (some payers use plan description to separate amount and total).
function adjustPeriodForDescription(benefit: BenefitItem): BenefitItem {
  if (
    'time_period' in benefit &&
    benefit.time_period === 'service_year' &&
    /^\d{4} ?APPLIED/.test(benefit.plan_description)
  ) {
    benefit.time_period = 'year_to_date';
  }
  return benefit;
}

// Tests to see if benefit should exist, given the current remaining deductible and the messages on the benefit.
function mismatchedAfterXDeductibleMessage(
  messages: EligibilityMessage[],
  network: BenefitNetwork,
  serviceTypes: string[],
  deductibles: EstimationBenefit.Deductible[],
): boolean {
  for (const message of messages) {
    if (
      message.message.includes(
        '100% COVERAGE - THEREAFTER PER CALENDAR YEAR FOR PLACE OF TRTMT',
      )
    ) {
      const spentDeductible = getSpentMatchingDeductible(
        deductibles,
        network,
        serviceTypes,
      );
      if (spentDeductible < 2000 && spentDeductible >= 0) {
        return true;
      }
    }
    if (
      message.message.includes(
        '80% COVERAGE - $0.01 TO $2000.00 PER CALENDAR YEAR FOR PLACE OF TRTMT',
      ) ||
      message.message.includes(
        '80% COVERAGE - THEREAFTER PER CALENDAR YEAR FOR PLACE OF TRMTMT',
      )
    ) {
      const spentDeductible = getSpentMatchingDeductible(
        deductibles,
        network,
        serviceTypes,
      );
      if (spentDeductible > 2000) {
        return true;
      }
    }
  }
  return false;
}

/**
 * This splits a Benefit Item we pulled from the x12 into one or more Benefit Pieces.
 * We create a Benefit Piece for each service type on the Benefit Item.
 */
function splitBenefit(
  benefit: BenefitItem,
  deductibles?: EstimationBenefit.Deductible[],
): BenefitPiece[] {
  benefit = adjustPeriodForDescription(benefit);
  const convertedNetwork = convertToBenefitNetwork(benefit);
  const convertedAmount = convertToBenefitAmount(benefit);
  const convertedPeriod = convertToBenefitPeriod(benefit);
  const messages = benefit.messages || [];
  const serviceTypes = benefit.service_type_codes;

  if (
    deductibles &&
    mismatchedAfterXDeductibleMessage(
      messages,
      convertedNetwork,
      serviceTypes,
      deductibles,
    )
  ) {
    return [];
  }

  // Here, we convert the Benefit Item into an array with a singular Benefit Piece.
  let newPieces: BenefitPiece[] = [
    {
      network: convertedNetwork,
      detail: {
        amount: convertedAmount,
        period: convertedPeriod,
      },
      level: benefit.coverage_level,
      note: messages.length
        ? messages
            .filter(Boolean)
            .map(m => m.message || '')
            .join(', ')
        : '',
      service: null,
      insuranceType: benefit.insurance_type,
      placeOfService: benefit.place_of_service || null,
      authRequired: benefit.auth_required,
      benefitQualifier: benefit.benefit_qualifier_info,
      timePeriod:
        'time_period' in benefit
          ? <BenefitTimePeriod>benefit.time_period
          : undefined, // convertToBenefitPeriod is far too lossy for certain purposes, like coinsurance maximums.
      planDescription: benefit.plan_description,
    },
  ];

  // This turns each existing piece in the array into one piece for each service type we have.
  if (serviceTypes != null && serviceTypes.length) {
    newPieces = flatMap(newPieces, record =>
      serviceTypes.map(service => ({ ...record, service })),
    );
  }
  return newPieces;
}

// This transforms a set of similar Benefit Pieces into a Benefit Record.
function combinePiecesIntoBenefitRecord(
  pieces: BenefitPiece[],
  differentLevelsCanGroup?: boolean,
  deprioritizeZeroAmounts?: boolean,
): BenefitRecord {
  // Takes the pieces and sorts them in order of relevance.
  const sorted = sortBy(
    pieces,
    piece => {
      switch (piece.detail.period) {
        case 'remaining':
          return 0;
        case 'lifetime_remaining':
          return 1;
        case 'amount':
        case 'visit':
          return 2;
        case 'lifetime':
        case 'year':
          return 3;
      }
    },
    piece => +lodashGet(piece, 'detail.amount.amount', 0),
  );

  let level: string | null;
  // Set the level to be the level of the remaining version of the benefit, and filter out any pieces that don't match that level.
  // We only care about the remaining version for calculations, which is why we keep those benefits.
  // We want this benefitRecord to be accurate for every benefit in it, so we can't keep benefits of the wrong level.
  if (differentLevelsCanGroup) {
    ({ level } = sorted[0]);
    pieces = pieces.filter(piece => piece.level === level);
  } else {
    level = pieces[0].level;
  }

  // Creates a Benefit Record out of the first of the unsorted pieces.
  // It doesn't matter which piece we use because all pieces should match on the relevant fields.
  const [common] = pieces;
  const record: BenefitRecord = {
    level,
    network: common.network,
    service: common.service,
    note: common.note,
    details: {},
    insuranceType: common.insuranceType,
    placeOfService: common.placeOfService,
    authRequired: common.authRequired,
    benefitQualifier: common.benefitQualifier,
  };

  // Fills the Record Details with the Benefit Amount associated with each Time Period.
  for (const benefit of sorted) {
    if (
      !record.details[benefit.detail.period] ||
      // RVT-4539 - choose the lowest benefit unless it is zero
      (deprioritizeZeroAmounts &&
        Number(record.details[benefit.detail.period]!.amount) === 0)
    ) {
      record.details[benefit.detail.period] =
        benefit.detail.amount == null ? undefined : benefit.detail.amount;
    }
  }

  return record;
}

// Puts similar benefit pieces together in arrays.
function groupBenefitPieces(
  benefitPieces: BenefitPiece[],
  differentLevelsCanGroup: boolean,
  differentValuesStaySeparate?: boolean,
  ignoreNotes?: boolean,
): BenefitPiece[][] {
  return Object.values(
    groupBy(benefitPieces, (piece: BenefitPiece) =>
      [
        piece.network,
        differentLevelsCanGroup ? '' : piece.level,
        ignoreNotes ? '' : piece.note,
        differentValuesStaySeparate
          ? +lodashGet(piece, 'detail.amount.amount', '')
          : '',
        piece.service,
        piece.placeOfService,
        piece.authRequired,
        piece.benefitQualifier ? piece.benefitQualifier : '',
      ].join('-'),
    ),
  );
}

// Looks to see which periods we actually used in the current pieces.
// The periods are only used in the benefit-table HTML, to choose the columns to display, and in what order.
function getSortedTimePeriods(pieces: BenefitPiece[]): BenefitPeriod[] {
  const periods = new Set<BenefitPeriod>(
    pieces.map(({ detail }) => detail.period),
  );
  const sortedPeriods: BenefitPeriod[] = [];
  for (const period of <BenefitPeriod[]>[
    'amount',
    'year',
    'year_to_date',
    'remaining',
    'lifetime',
    'lifetime_remaining',
    'visit',
  ]) {
    if (periods.has(period)) {
      sortedPeriods.push(period);
    }
  }
  return sortedPeriods;
}

// Fills in all the missing pieces. This is it's own function so we can map to it easily.
function fillMissingPieces(benefit: BenefitRecord): BenefitRecord {
  benefit = fillMissingBenefitPeriods(benefit);
  benefit = fillMissingServiceTypes(benefit);
  return benefit;
}

// Fills in a missing value for total or remaining if we have the amount of the deductible used.
function fillMissingBenefitPeriods(benefit: BenefitRecord): BenefitRecord {
  const yearToDate = getDeductibleUsedFromRecord(benefit);
  const remaining = getDeductibleRemainingFromRecord(benefit);
  const total = getDeductibleTotalFromRecord(benefit);

  if (!yearToDate || (!remaining && !total)) {
    return benefit;
  }

  if (remaining) {
    const filledTotal = +remaining.amount + +yearToDate.amount;
    const filledTotalAmount: BenefitAmount = {
      type: 'currency',
      amount: filledTotal.toString(),
      currency: 'USD',
    };
    if (!benefit.details['year']) {
      benefit.details['year'] = filledTotalAmount;
    }
    return benefit;
  }

  if (total) {
    const filledRemaining = Math.max(+total.amount - +yearToDate.amount, 0);
    const filledRemainingAmount: BenefitAmount = {
      type: 'currency',
      amount: filledRemaining.toString(),
      currency: 'USD',
    };
    if (!benefit.details['remaining']) {
      benefit.details['remaining'] = filledRemainingAmount;
    }
    return benefit;
  }

  return benefit;
}

// Fills in a service type as Health Benefit Plan Coverage if we don't receive a service type.
function fillMissingServiceTypes(benefit: BenefitRecord): BenefitRecord {
  if (benefit.service == null) {
    benefit.service = '30';
  }

  return benefit;
}

function recordHasAllZeroes(benefit: BenefitRecord): boolean {
  return (
    (!benefit.details.remaining ||
      benefit.details.remaining.amount == '0.00') &&
    (!benefit.details.year || benefit.details.year.amount == '0.00') &&
    (!benefit.details.year_to_date ||
      benefit.details.year_to_date.amount == '0.00') &&
    (!benefit.details.amount || benefit.details.amount.amount == '0.00') &&
    (!benefit.details.visit || benefit.details.visit.amount == '0.00') &&
    (!benefit.details.lifetime || benefit.details.lifetime.amount == '0.00') &&
    (!benefit.details.lifetime_remaining ||
      benefit.details.lifetime_remaining.amount == '0.00')
  );
}

// Filters any benefit records that are not-applicable where every existing value is 0.
function filterNotApplicableZeroes(benefit: BenefitRecord): boolean {
  return !(benefit.network == 'not_applicable' && recordHasAllZeroes(benefit));
}

// Filters any $0/$0 benefits of the specified level
// when we have a similar record of another level
function filterIdenticalAllZeroes(
  records: BenefitRecord[],
  level: CoverageLevel,
): BenefitRecord[] {
  const recordsMatch = (
    record1: BenefitRecord,
    record2: BenefitRecord,
  ): boolean =>
    record1.service === record2.service &&
    record1.note === record2.note &&
    record1.placeOfService === record2.placeOfService &&
    record1.network === record2.network;

  const [zeroRecords, otherLevelRecords] = lodashPartition(
    records,
    record => record.level === level && recordHasAllZeroes(record),
  );

  return [
    ...otherLevelRecords,
    ...zeroRecords.filter(
      zr => !otherLevelRecords.some(r => recordsMatch(zr, r)),
    ),
  ];
}

// Adjusts benefits we receive with negative remaining amounts - RVT-5144
function handleNegativeRemainingAmounts(benefit: BenefitRecord): BenefitRecord {
  const remaining = getDeductibleRemainingFromRecord(benefit);
  // Only adjust if the remaining amount is negative.
  if (!remaining || +remaining.amount >= 0) {
    return benefit;
  }

  const total = getDeductibleTotalFromRecord(benefit);

  // If we have a negative amount and a total, add the absolute value of the amount to the total.
  if (total) {
    const totalPlusRemaining: number =
      Math.abs(+remaining.amount) + +total.amount;
    const totalPlusRemainingAmount: BenefitAmount = {
      type: 'currency',
      amount: totalPlusRemaining.toString(),
      currency: 'USD',
    };
    // Set the total to the combined total.
    if (benefit.details['year']) {
      benefit.details['year'] = totalPlusRemainingAmount;
    }
    if (benefit.details['lifetime']) {
      benefit.details['lifetime'] = totalPlusRemainingAmount;
    }
    if (benefit.details['amount']) {
      benefit.details['amount'] = totalPlusRemainingAmount;
    }
    // Set the remaining to the combined total.
    if (benefit.details['remaining']) {
      benefit.details['remaining'] = totalPlusRemainingAmount;
    }
    if (benefit.details['lifetime_remaining']) {
      benefit.details['lifetime_remaining'] = totalPlusRemainingAmount;
    }
    return benefit;
  }

  // If we have a negative amount and no total, use 0 for the amount.
  if (!total) {
    const zeroRemainingAmount: BenefitAmount = {
      type: 'currency',
      amount: '0',
      currency: 'USD',
    };
    if (benefit.details['remaining']) {
      benefit.details['remaining'] = zeroRemainingAmount;
    }
    if (benefit.details['lifetime_remaining']) {
      benefit.details['lifetime_remaining'] = zeroRemainingAmount;
    }
    return benefit;
  }

  return benefit;
}

export function groupAllBenefits(
  eligibility: EligibilityData,
  hcaCoveredServiceTypeCodes: string[] = [],
): {
  copays: GroupedBenefits;
  coinsurances: GroupedBenefits;
  deductibles: GroupedBenefits;
  oops: GroupedBenefits;
  limitations: GroupedBenefits;
} {
  const deductibles = groupBenefits(eligibility.coverage.deductibles || [], {
    differentLevelsCanGroup: false,
    hcaCoveredServiceTypeCodes: hcaCoveredServiceTypeCodes,
    deprioritizeZeroAmounts: true,
    recombineWhileIgnoringComments: true,
  });

  const coinsurances = combineCoinsuranceAndLimitations(
    eligibility.coverage.coinsurance || [],
    eligibility.coverage.limitations || [],
    {
      differentLevelsCanGroup: true,
      differentNotesCanGroup: false,
      deductibles: deductibles.benefits.map(deductible),
      differentValuesStaySeparate: true,
      hcaCoveredServiceTypeCodes: hcaCoveredServiceTypeCodes,
    },
  );

  const copays = groupBenefits(eligibility.coverage.copay || [], {
    differentLevelsCanGroup: true,
    differentValuesStaySeparate: true,
    hcaCoveredServiceTypeCodes: hcaCoveredServiceTypeCodes,
  });

  const oops = groupBenefits(eligibility.coverage.out_of_pocket || [], {
    differentLevelsCanGroup: false,
    hcaCoveredServiceTypeCodes: hcaCoveredServiceTypeCodes,
    deprioritizeZeroAmounts: true,
    recombineWhileIgnoringComments: true,
  });

  const limitations = groupBenefits(eligibility.coverage.limitations || [], {
    differentLevelsCanGroup: false,
    hcaCoveredServiceTypeCodes: hcaCoveredServiceTypeCodes,
  });

  return {
    copays,
    coinsurances,
    deductibles,
    oops,
    limitations,
  };
}

/**
 * Group benefits by parameters
 */
export function groupBenefits(
  benefits: BenefitItem[],
  options: {
    differentLevelsCanGroup: boolean;
    differentValuesStaySeparate?: boolean;
    deductibles?: EstimationBenefit.Deductible[];
    hcaCoveredServiceTypeCodes?: string[];
    deprioritizeZeroAmounts?: boolean;
    recombineWhileIgnoringComments?: boolean;
  },
): GroupedBenefits {
  let pieces: BenefitPiece[] = [];

  // Splits each Benefit Item into its corresponding Benefit Pieces.
  for (const benefit of benefits) {
    pieces.push(...splitBenefit(benefit, options.deductibles));
  }

  pieces = filterDentalVisionPieces(pieces);

  // Creates an array where each element is an array of similar Benefit Pieces.
  let groups = groupBenefitPieces(
    pieces,
    options.differentLevelsCanGroup,
    options.differentValuesStaySeparate,
  );

  // RVT-4454 - if we were unable to group pieces, try again
  // while ignoring free form text
  if (
    groups.length === pieces.length &&
    options.recombineWhileIgnoringComments
  ) {
    groups = groupBenefitPieces(
      pieces,
      options.differentLevelsCanGroup,
      options.differentValuesStaySeparate,
      true,
    );
  }

  // Creates a Benefit Record out of each group
  let records = groups.map(pieces =>
    combinePiecesIntoBenefitRecord(
      pieces,
      options.differentLevelsCanGroup,
      options.deprioritizeZeroAmounts,
    ),
  );

  if (options.hcaCoveredServiceTypeCodes) {
    records = records.concat(
      options.hcaCoveredServiceTypeCodes.map(hcaBenefit),
    );
  }

  records = records.map(fillMissingPieces);
  // RVT-5144 - Handle negative remaining amounts
  records = records.map(handleNegativeRemainingAmounts);
  records = records.filter(filterNotApplicableZeroes);

  if (!options.differentLevelsCanGroup) {
    // RVT-6651 Remove Family Deductible $0/$0's
    records = filterIdenticalAllZeroes(records, 'family');
    // RVT-4257 Remove Individual Deductible $0/$0's
    records = filterIdenticalAllZeroes(records, 'individual');
  }

  return { periods: getSortedTimePeriods(pieces), benefits: records };
}

// Removes pieces with plan descriptions containing 'dental' or 'vision'
function filterDentalVisionPieces(pieces: BenefitPiece[]) {
  return pieces.filter(
    piece => !/dental|vision/.test(piece.planDescription?.toLowerCase() || ''),
  );
}

// Converts a Benefit Record into an Eligibility Benefit
function recordToEligibilityBenefit(
  benefit: BenefitRecord | null,
): EligibilityBenefit {
  return {
    level: benefit && benefit.level,
    comment: benefit && benefit.note,
    network: benefit && benefit.network,
    service: benefit && benefit.service,
    insuranceType: benefit && benefit.insuranceType,
    placeOfService: benefit && benefit.placeOfService,
    ...(benefit && benefit.authRequired
      ? { authRequired: benefit.authRequired }
      : {}),
    ...(benefit && benefit.benefitQualifier
      ? { benefitQualifier: benefit.benefitQualifier }
      : {}),
  };
}

// Get the amount of money spent on the matching deductible, where matching means: same service type and network. Coverage Level is not applied because we don't always group by it, so the deductibles may not have an accurate level.
function getSpentMatchingDeductible(
  deductibles: EstimationBenefit.Deductible[],
  network: BenefitNetwork,
  serviceTypes: string[],
) {
  for (const deductible of deductibles) {
    if (
      deductible.eligibilityBenefit &&
      deductible.eligibilityBenefit.network == network
    ) {
      if (
        deductible.eligibilityBenefit.service &&
        serviceTypes.includes(deductible.eligibilityBenefit.service)
      ) {
        if (deductible.total && deductible.amount) {
          return Number(deductible.total - deductible.amount);
        }
      }
    }
  }
  // If there is no matching deductible or we cannot determine the amount, we arbitrarily show the 80% message value. It can only get better for the customer from there.
  return 0;
}

export function copay(benefit: BenefitRecord): EstimationBenefit.Copay {
  const amount = (benefit && benefit.details.amount) || benefit.details.visit;
  return {
    amount: amount == null ? null : +amount.amount,
    eligibilityBenefit: recordToEligibilityBenefit(benefit),
    custom: false,
  };
}

export function coinsurance(
  benefit: BenefitRecord,
): EstimationBenefit.Coinsurance {
  const amount =
    benefit &&
    (benefit.details.amount || benefit.details.visit || benefit.details.year);
  return {
    eligibilityBenefit: recordToEligibilityBenefit(benefit),
    proportion: amount == null ? null : +amount.amount,
    custom: false,
  };
}

function getDeductibleRemainingFromRecord(
  benefit: BenefitRecord,
): BenefitAmount | undefined {
  return (
    benefit && (benefit.details.remaining || benefit.details.lifetime_remaining)
  );
}

function getDeductibleUsedFromRecord(
  benefit: BenefitRecord,
): BenefitAmount | undefined {
  return benefit && benefit.details.year_to_date;
}

function getDeductibleTotalFromRecord(
  benefit: BenefitRecord,
): BenefitAmount | undefined {
  return (
    benefit &&
    (benefit.details.year || benefit.details.lifetime || benefit.details.amount)
  );
}

export function deductible(
  benefit: BenefitRecord,
): EstimationBenefit.Deductible {
  const amount = getDeductibleRemainingFromRecord(benefit);
  const yearToDate = getDeductibleUsedFromRecord(benefit);
  const total = getDeductibleTotalFromRecord(benefit);
  return {
    eligibilityBenefit: recordToEligibilityBenefit(benefit),
    amount:
      amount == null
        ? yearToDate && total
          ? +total.amount - +yearToDate.amount
          : null
        : +amount.amount,
    total:
      total == null
        ? amount && yearToDate
          ? +amount.amount + +yearToDate.amount
          : null
        : +total.amount,
    custom: false,
    cascadingBenefit: false,
  };
}

export function oop(benefit: BenefitRecord): EstimationBenefit.Oop {
  return deductible(benefit);
}

export function getBenefitAmountAsNumber<
  T extends EstimationBenefit.AnyBenefit,
>(benefit: T): number {
  let amount: number | null = 0;
  if ((<EstimationBenefit.Copay>benefit).amount != null) {
    amount = (<EstimationBenefit.Copay>benefit).amount;
  }
  if ((<EstimationBenefit.Coinsurance>benefit).proportion != null) {
    amount = (<EstimationBenefit.Coinsurance>benefit).proportion;
  }
  if ((<EstimationBenefit.Deductible>benefit).total != null) {
    amount = (<EstimationBenefit.Deductible>benefit).total;
  }
  if ((<EstimationBenefit.Oop>benefit).total != null) {
    amount = (<EstimationBenefit.Oop>benefit).total;
  }
  if (!amount) {
    amount = 0;
  }
  return Number(amount);
}

// Returns whether the benefit amount is the same on both benefits
function compareBenefitsOnAmount<T extends EstimationBenefit.AnyBenefit>(
  firstBenefit: T,
  secondBenefit: T,
): boolean {
  /*
    Amount is used on copay, deductible, oop
    Total is used on deductible, oop
    Proportion is used on coinsurance
  */
  return (
    lodashGet(firstBenefit, 'amount') == lodashGet(secondBenefit, 'amount') &&
    lodashGet(firstBenefit, 'total') == lodashGet(secondBenefit, 'total') &&
    lodashGet(firstBenefit, 'proportion') ==
      lodashGet(secondBenefit, 'proportion')
  );
}

// Returns the closest benefit in the list of benefits to the selected benefit.
export function selectClosestBenefit<T extends EstimationBenefit.AnyBenefit>(
  selectedBenefit: EstimationBenefit.AnyBenefit,
  benefits: T[],
): T | null {
  const eligibilityBenefit = selectedBenefit.eligibilityBenefit;

  return eligibilityBenefit != null
    ? benefits.find(
        benefit =>
          compareBenefitsOnAmount(benefit, selectedBenefit) &&
          eligibilityBenefit.comment ==
            lodashGet(benefit, 'eligibilityBenefit.comment') &&
          eligibilityBenefit.network ==
            lodashGet(benefit, 'eligibilityBenefit.network') &&
          eligibilityBenefit.placeOfService ==
            lodashGet(benefit, 'eligibilityBenefit.placeOfService') &&
          eligibilityBenefit.service ==
            lodashGet(benefit, 'eligibilityBenefit.service'),
      ) || null
    : null;
}

export type EligibilityBenefits = {
  copay: EstimationBenefit.Copay[];
  coinsurance: EstimationBenefit.Coinsurance[];
  deductible: EstimationBenefit.Deductible[];
  oop: EstimationBenefit.Oop[];
  limitation: EstimationBenefit.Limitation[];
  otherLimitation: EstimationBenefit.Limitation[];
};

export const emptyEligibilityBenefits: EligibilityBenefits = {
  copay: [],
  coinsurance: [],
  deductible: [],
  oop: [],
  limitation: [],
  otherLimitation: [],
};

// Converts the data from the eligibility response into a set of grouped/combined benefits.
export function eligibilityResponseToBenefits(
  response: InterpretedEligibilityResponse | null,
  hcaCoveredServiceTypeCodes: string[] = [],
  selectedServiceType?: string,
): EligibilityBenefits {
  if (!response) {
    return emptyEligibilityBenefits;
  }
  const data = lodashGet(response, ['data']);
  return data
    ? eligibilityDataToBenefits(
        data,
        hcaCoveredServiceTypeCodes,
        selectedServiceType,
      )
    : {
        ...emptyEligibilityBenefits,
      };
}

function eligibilityDataToBenefits(
  eligibility: EligibilityData,
  hcaCoveredServiceTypeCodes: string[] = [],
  selectedServiceType?: string,
): EligibilityBenefits {
  if (!eligibility.coverage) {
    return {
      copay: [],
      coinsurance: [],
      deductible: [],
      oop: [],
      limitation: [],
      otherLimitation: [],
    };
  }

  const groupedBenefits = groupAllBenefits(
    eligibility,
    hcaCoveredServiceTypeCodes,
  );

  const deductibles: EstimationBenefit.Deductible[] =
    groupedBenefits.deductibles.benefits.map(deductible);
  const coinsurances: EstimationBenefit.Coinsurance[] =
    groupedBenefits.coinsurances.benefits.map(coinsurance);
  const copays: EstimationBenefit.Copay[] =
    groupedBenefits.copays.benefits.map(copay);

  const oops: EstimationBenefit.Oop[] = groupedBenefits.oops.benefits
    .map(oop)
    .filter(oop => {
      return !(
        oop.eligibilityBenefit?.network == 'out' &&
        oop.total == 0 &&
        oop.amount == 0
      );
    });

  const limitationRecords = createReadableLimitations(
    eligibility.coverage,
    eligibility.benefitDescriptions,
    selectedServiceType,
    true,
  );

  const limitations: EstimationBenefit.Limitation[] =
    limitationRecords.readableLimitations
      .map(limit => recordToEligibilityBenefit(limit))
      .map(limit => limitationToBenefitLimitation(limit));

  const otherLimitations: EstimationBenefit.Limitation[] =
    limitationRecords.otherLimitations;

  return {
    copay: copays || [],
    coinsurance: coinsurances || [],
    deductible: deductibles || [],
    oop: oops || [],
    limitation: limitations || [],
    otherLimitation: otherLimitations || [],
  };
}

function properPercent(value: number) {
  if (value > 1) {
    return value / 100;
  }
  return value;
}

export function mapNetworkAndLevel(
  coverageLevel: CoverageLevel,
  coverageNetwork: InNetworkIndicator,
) {
  const mapKey: string = '' + coverageLevel + '-' + coverageNetwork;
  return mapKey;
}

export function getAuthForBenefits(
  benefits: EstimationBenefit | undefined,
): AuthRequired | null {
  if (benefits) {
    if (
      benefits.coinsurance.eligibilityBenefit?.authRequired == 'yes' ||
      benefits.copay.eligibilityBenefit?.authRequired == 'yes' ||
      benefits.deductible.eligibilityBenefit?.authRequired == 'yes' ||
      benefits.limitation.eligibilityBenefit?.authRequired == 'yes' ||
      benefits.oop.eligibilityBenefit?.authRequired == 'yes'
    ) {
      return 'yes';
    } else if (
      benefits.coinsurance.eligibilityBenefit?.authRequired == 'unknown' ||
      benefits.copay.eligibilityBenefit?.authRequired == 'unknown' ||
      benefits.deductible.eligibilityBenefit?.authRequired == 'unknown' ||
      benefits.limitation.eligibilityBenefit?.authRequired == 'unknown' ||
      benefits.oop.eligibilityBenefit?.authRequired == 'unknown'
    ) {
      return 'unknown';
    }
  }
  return null;
}

export interface PlaceOfService {
  name: string;
  facilityType: FacilityType;
}

export enum PlaceOfServiceCode {
  PHARMACY = '01',
  TELEHEALTH = '02',
  SCHOOL = '03',
  HOMELESS_SHELTER = '04',
  INDIAN_HEALTH_SERVICE_FREE_STANDING_FACILITY = '05',
  INDIAN_HEALTH_SERVICE_PROVIDER_BASED_FACILITY = '06',
  TRIBAL_FREE_STANDING_FACILIY = '07',
  TRIBAL_PROVIDER_BASED_FACILITY = '08',
  PRISON = '09',
  TELEHEALTH_HOME = '10',
  OFFICE = '11',
  HOME = '12',
  ASSISTED_LIVING = '13',
  GROUP_HOME = '14',
  MOBILE_UNIT = '15',
  TEMPORARY_LODGING = '16',
  WALKIN_RETAIL_HEALTH_CLINIC = '17',
  OUTPATIENT_HOSPITAL_OFF_CAMPUS = '19',
  URGENT_CARE_FACILITY = '20',
  INPATIENT_HOSPITAL = '21',
  OUTPATIENT_HOSPITAL = '22',
  EMERGENCY_ROOM_HOSPITAL = '23',
  AMBULATORY_SURGICAL_CENTER = '24',
  BIRTHING_CENTER = '25',
  MILITARY_TREATMENT_FACILITY = '26',
  SKILLED_NURSING_FACILITY = '31',
  NURSING_FACILITY = '32',
  CUSTODIAL_CARE_FACILITY = '33',
  HPOSPICE = '34',
  AMBULANCE_LAND = '41',
  AMBULANCE_AIR_OR_WATER = '42',
  INDEPENDENT_CLINIC = '49',
  FEDERALLY_QUALIFIED_HEALTH_CENTER = '50',
  INPATIENT_PSYCHIATRIC_FACILITY = '51',
  PSYCHIATRIC_FACILITY_PARTIAL_HOSPITALIZATION = '52',
  COMMUNINTY_MENTAL_HEALTH_CENTER = '53',
  INTERMEDIATE_CARE_FACILITY = '54',
  RESIDENTAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY = '55',
  PSYCHIATRIC_RESIDENTIAL_TREATMENT_CENTER = '56',
  NONRESIDENTIAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY = '57',
  MASS_IMMUNIZATION_CENTER = '60',
  COMPREHENSIVE_INPATIENT_REHABILITATION_FACILITY = '61',
  COMPREHENSIVE_OUTPATIENT_REHABILITATION_FACILITY = '62',
  END_STAGE_RENAL_DISEASE_TREATMENT_FACILITY = '65',
  STATE_OR_LOCAL_PUBLIC_HEALTH_CLINIC = '71',
  RURAL_HEALTH_CLINIC = '72',
  INDEPENDENT_LABORATORY = '81',
  OTHER = '99',
  NO_PLACE_OF_SERVICE = 'no_place_of_service',
}

export function toPlaceOfServiceCode(
  placeOfServiceCode: string,
): PlaceOfServiceCode | null {
  // zero pad to handle e.g. '1'
  placeOfServiceCode = (placeOfServiceCode || '').padStart(2, '0');
  return isPlaceOfService(placeOfServiceCode) ? placeOfServiceCode : null;
}

export function isPlaceOfService(
  placeOfService: string,
): placeOfService is PlaceOfServiceCode {
  return Object.values(PlaceOfServiceCode).includes(
    placeOfService as PlaceOfServiceCode,
  );
}

export const PlacesOfService: Map<PlaceOfServiceCode, PlaceOfService> = new Map(
  [
    [
      PlaceOfServiceCode.PHARMACY,
      { name: 'Pharmacy', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.TELEHEALTH,
      { name: 'Telehealth', facilityType: 'facility' },
    ],
    [
      PlaceOfServiceCode.SCHOOL,
      { name: 'School', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.HOMELESS_SHELTER,
      { name: 'Homeless Shelter', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.INDIAN_HEALTH_SERVICE_FREE_STANDING_FACILITY,
      {
        name: 'Indian Health Service Free-Standing Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.INDIAN_HEALTH_SERVICE_PROVIDER_BASED_FACILITY,
      {
        name: 'Indian Health Service Provider-Based Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.TRIBAL_FREE_STANDING_FACILIY,
      {
        name: 'Tribal 638 Free-Standing Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.TRIBAL_PROVIDER_BASED_FACILITY,
      {
        name: 'Tribal 638 Provider-Based Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.PRISON,
      { name: 'Prison/Correctional Facility', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.TELEHEALTH_HOME,
      { name: 'Telehealth (Home)', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.OFFICE,
      { name: 'Office', facilityType: 'non-facility' },
    ],
    [PlaceOfServiceCode.HOME, { name: 'Home', facilityType: 'non-facility' }],
    [
      PlaceOfServiceCode.ASSISTED_LIVING,
      { name: 'Assisted Living Facility', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.GROUP_HOME,
      { name: 'Group Home', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.MOBILE_UNIT,
      { name: 'Mobile Unit', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.TEMPORARY_LODGING,
      { name: 'Temporary Lodging', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.WALKIN_RETAIL_HEALTH_CLINIC,
      { name: 'Walk-in Retail Health Clinic', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.OUTPATIENT_HOSPITAL_OFF_CAMPUS,
      {
        name: 'Outpatient Hospital - Off Campus',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.URGENT_CARE_FACILITY,
      { name: 'Urgent Care Facility', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.INPATIENT_HOSPITAL,
      { name: 'Inpatient Hospital', facilityType: 'facility' },
    ],
    [
      PlaceOfServiceCode.OUTPATIENT_HOSPITAL,
      { name: 'Outpatient Hospital', facilityType: 'facility' },
    ],
    [
      PlaceOfServiceCode.EMERGENCY_ROOM_HOSPITAL,
      {
        name: 'Emergency Room-Hospital',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.AMBULATORY_SURGICAL_CENTER,
      {
        name: 'Ambulatory Surgical Center',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.BIRTHING_CENTER,
      { name: 'Birthing Center', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.MILITARY_TREATMENT_FACILITY,
      {
        name: 'Military Treatment Facility',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.SKILLED_NURSING_FACILITY,
      {
        name: 'Skilled Nursing Facility',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.NURSING_FACILITY,
      { name: 'Nursing Facility', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.CUSTODIAL_CARE_FACILITY,
      { name: 'Custodial Care Facility', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.HPOSPICE,
      { name: 'Hospice', facilityType: 'facility' },
    ],
    [
      PlaceOfServiceCode.AMBULANCE_LAND,
      { name: 'Ambulance-Land', facilityType: 'facility' },
    ],
    [
      PlaceOfServiceCode.AMBULANCE_AIR_OR_WATER,
      { name: 'Ambulance-Air or Water', facilityType: 'facility' },
    ],
    [
      PlaceOfServiceCode.INDEPENDENT_CLINIC,
      { name: 'Independent Clinic', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.FEDERALLY_QUALIFIED_HEALTH_CENTER,
      {
        name: 'Federally Qualified Health Center',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.INPATIENT_PSYCHIATRIC_FACILITY,
      {
        name: 'Inpatient Psychiatric Facility',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.PSYCHIATRIC_FACILITY_PARTIAL_HOSPITALIZATION,
      {
        name: 'Psychiatric Facility-Partial Hospitalization',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.COMMUNINTY_MENTAL_HEALTH_CENTER,
      {
        name: 'Community Mental Health Center',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.INTERMEDIATE_CARE_FACILITY,
      {
        name: 'Intermediate Care Facility/Individuals with Intellectual Disabilities',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.RESIDENTAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY,
      {
        name: 'Residental Substance Abuse Treatment Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.PSYCHIATRIC_RESIDENTIAL_TREATMENT_CENTER,
      {
        name: 'Psychiatric Residental Treatment Center',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.NONRESIDENTIAL_SUBSTANCE_ABUSE_TREATMENT_FACILITY,
      {
        name: 'Non-residental Substance Abuse Treatment Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.MASS_IMMUNIZATION_CENTER,
      { name: 'Mass Immunization Center', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.COMPREHENSIVE_INPATIENT_REHABILITATION_FACILITY,
      {
        name: 'Comprehensive Inpatient Rehabilitation Facility',
        facilityType: 'facility',
      },
    ],
    [
      PlaceOfServiceCode.COMPREHENSIVE_OUTPATIENT_REHABILITATION_FACILITY,
      {
        name: 'Comprehensive Outpatient Rehabilitation Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.END_STAGE_RENAL_DISEASE_TREATMENT_FACILITY,
      {
        name: 'End Stage Renal Disease Treatment Facility',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.STATE_OR_LOCAL_PUBLIC_HEALTH_CLINIC,
      {
        name: 'State or Local Public Health Clinic',
        facilityType: 'non-facility',
      },
    ],
    [
      PlaceOfServiceCode.RURAL_HEALTH_CLINIC,
      { name: 'Rural Health Clinic', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.INDEPENDENT_LABORATORY,
      { name: 'Independent Laboratory', facilityType: 'non-facility' },
    ],
    [
      PlaceOfServiceCode.OTHER,
      { name: 'Other Place of Service', facilityType: 'non-facility' },
    ],
  ],
);

// Combines in the applicable limitations that match coinsurances with maximums.
function combineCoinsuranceAndLimitations(
  coinsurances: Coinsurance[],
  limitations: Limitation[],
  options: {
    differentLevelsCanGroup: boolean;
    differentNotesCanGroup: boolean;
    differentValuesStaySeparate?: boolean;
    deductibles?: EstimationBenefit.Deductible[];
    hcaCoveredServiceTypeCodes?: string[];
  },
): GroupedBenefits {
  // First, split the coinsurances into benefit pieces.
  let coinsurancePieces: BenefitPiece[] = [];
  for (const coinsurance of coinsurances) {
    coinsurancePieces.push(...splitBenefit(coinsurance, options.deductibles));
  }

  // Then, merge in the limitations that have a matching time-period and are similar to the coinsurance.
  for (const limitation of limitations) {
    if (
      limitation.quantity_qualifier == 'visits' ||
      limitation.quantity_qualifier == 'days' ||
      limitation.quantity_qualifier == 'none' ||
      limitation.time_period_qualifier == 'no_recognized_time_period'
    ) {
      continue;
    }

    let matchedTimePeriods: string[] = [];
    switch (limitation.time_period_qualifier) {
      case 'remaining':
        matchedTimePeriods = ['calendar_year', 'service_year', 'year', 'week'];
        break;
      case 'lifetime_remaining':
        matchedTimePeriods = ['lifetime'];
        break;
      default:
        matchedTimePeriods = [limitation.time_period_qualifier];
        break;
    }

    // Merge limitations on time period, service type, and network.
    coinsurancePieces = coinsurancePieces.map(coinsurancePiece => {
      if (
        coinsurancePiece.timePeriod &&
        matchedTimePeriods.includes(coinsurancePiece.timePeriod) &&
        coinsurancePiece.service &&
        limitation.service_type_codes.includes(coinsurancePiece.service) &&
        convertToBenefitNetwork(limitation) == coinsurancePiece.network
      ) {
        if (
          coinsurancePiece.benefitQualifier &&
          coinsurancePiece.benefitQualifier.quantity_qualifier == 'maximum'
        ) {
          coinsurancePiece.benefitQualifier = {
            ...coinsurancePiece.benefitQualifier,
            quantity_remaining: limitation.quantity,
          };
        } else {
          // If there was not already a maximum, the limitation counts as the total and remaining on the maximum.
          coinsurancePiece = {
            ...coinsurancePiece,
            benefitQualifier: {
              time_period: coinsurancePiece.timePeriod,
              quantity_remaining: limitation.quantity,
              quantity: limitation.quantity.toString(),
              quantity_qualifier: 'maximum',
            },
          };
        }
      }
      return coinsurancePiece;
    });
  }

  // Set the remaining amount to be equal to the total if none exists, so the estimate calculation works correctly.
  coinsurancePieces = coinsurancePieces.map(coinsurancePiece => {
    if (
      coinsurancePiece.benefitQualifier &&
      coinsurancePiece.benefitQualifier.quantity_qualifier == 'maximum' &&
      coinsurancePiece.benefitQualifier.quantity &&
      !coinsurancePiece.benefitQualifier.quantity_remaining
    ) {
      coinsurancePiece = {
        ...coinsurancePiece,
        benefitQualifier: {
          ...coinsurancePiece.benefitQualifier,
          quantity_remaining: +coinsurancePiece.benefitQualifier.quantity,
          qualifier_amount: coinsurancePiece.benefitQualifier?.qualifier_amount,
        },
      };
    }
    return coinsurancePiece;
  });

  // Finally, group the coinsurance pieces and create a Benefit Record out of the groups.
  const coinsuranceGroups = groupBenefitPieces(
    coinsurancePieces,
    options.differentLevelsCanGroup,
    options.differentValuesStaySeparate,
  );
  let coinsuranceRecords = coinsuranceGroups.map(pieces =>
    combinePiecesIntoBenefitRecord(pieces, options.differentLevelsCanGroup),
  );

  if (options.hcaCoveredServiceTypeCodes) {
    coinsuranceRecords = coinsuranceRecords.concat(
      options.hcaCoveredServiceTypeCodes.map(hcaBenefit),
    );
  }

  return {
    benefits: coinsuranceRecords,
    periods: getSortedTimePeriods(coinsurancePieces),
  };
}

interface MatchedBenefitPiece extends BenefitPiece {
  matched?: boolean;
}

// Converts the limitations and coinsurance limitations into an easier format for displaying in a table.
export function createReadableLimitations(
  coverage: Coverage,
  benefitDescriptions?: BenefitDescription[],
  selectedServiceType?: string,
  onlyShowNumberOfXLimitations?: boolean,
): LimitationRecords {
  if (!coverage) {
    return { readableLimitations: [], otherLimitations: [] };
  }
  // First, split the coinsurances and limitations into benefit pieces.
  const limitedCoinsurances: Coinsurance[] =
    (coverage.coinsurance &&
      coverage.coinsurance.filter(
        coinsurance => coinsurance.benefit_qualifier_info,
      )) ||
    [];
  const coinsurancePieces: MatchedBenefitPiece[] = [];
  for (const coinsurance of limitedCoinsurances) {
    coinsurancePieces.push(...splitBenefit(coinsurance));
  }
  coinsurancePieces
    .filter(coinsurance => coinsurance.service && coinsurance.timePeriod)
    .map(coinsurance => (coinsurance.matched = false));

  // Move the limitation information into the benefit qualifier, so it doesn't get lost when we split the benefit.
  const limitedLimitations: Limitation[] =
    (coverage.limitations &&
      coverage.limitations.map(limitation => {
        return {
          ...limitation,
          benefit_qualifier_info: limitation.benefit_qualifier_info || {
            time_period: limitation.time_period_qualifier,
            quantity: limitation.quantity?.toString(),
            quantity_qualifier: limitation.quantity_qualifier,
            qualifier_amount: limitation?.qualifier_amount,
          },
        };
      })) ||
    [];
  const limitationPieces: BenefitPiece[] = [];
  for (const limitation of limitedLimitations) {
    limitationPieces.push(...splitBenefit(limitation));
  }

  const mergedAndParsedLimitationPieces: BenefitPiece[] = [];
  const otherLimitationPieces: BenefitPiece[] = [];
  // Then, merge the coinsurance information into the limitations.
  for (const limitation of limitationPieces) {
    if (!limitation.benefitQualifier || !limitation.benefitQualifier.quantity) {
      if (limitation.note) {
        otherLimitationPieces.push(limitation);
      }
      continue;
    }
    if (
      onlyShowNumberOfXLimitations &&
      limitation.benefitQualifier.quantity_qualifier != 'visits' &&
      limitation.benefitQualifier.quantity_qualifier != 'days' &&
      limitation.benefitQualifier.quantity_qualifier != 'medicare-used'
    ) {
      if (limitation.note) {
        otherLimitationPieces.push(limitation);
      }
      continue;
    }

    let quantityQualToCompare: string[] = [];
    switch (limitation.benefitQualifier.quantity_qualifier) {
      case 'visits':
        quantityQualToCompare = ['visits'];
        break;
      case 'days':
        quantityQualToCompare = ['days'];
        break;
      case 'years':
        quantityQualToCompare = ['years'];
        break;
      case 'maximum':
      case 'none':
        quantityQualToCompare = ['maximum', 'none'];
        break;
    }
    let matchedTimePeriods: string[] = [];
    switch (limitation.benefitQualifier.time_period) {
      case 'remaining':
        matchedTimePeriods = ['calendar_year', 'service_year', 'year', 'week'];
        break;
      case 'lifetime_remaining':
        matchedTimePeriods = ['lifetime'];
        break;
      case 'calendar_year':
      case 'service_year':
      case 'year':
      case 'week':
        matchedTimePeriods = ['remaining'];
        break;
      case 'lifetime':
        matchedTimePeriods = ['lifetime_remaining'];
        break;
      default:
        matchedTimePeriods = [];
        break;
    }

    let hasMerged: boolean = false;
    // Merge limitations on time period, service type, and network.
    for (const coinsurancePiece of coinsurancePieces) {
      if (
        coinsurancePiece.benefitQualifier &&
        coinsurancePiece.benefitQualifier.quantity_qualifier &&
        quantityQualToCompare.includes(
          coinsurancePiece.benefitQualifier.quantity_qualifier,
        ) &&
        coinsurancePiece.timePeriod &&
        matchedTimePeriods.includes(coinsurancePiece.timePeriod) &&
        coinsurancePiece.service &&
        limitation.service == coinsurancePiece.service &&
        limitation.network == coinsurancePiece.network &&
        limitation.note == coinsurancePiece.note
      ) {
        mergedAndParsedLimitationPieces.push({
          ...limitation,
          benefitQualifier: {
            time_period: coinsurancePiece.benefitQualifier.time_period,
            quantity_qualifier:
              coinsurancePiece.benefitQualifier.quantity_qualifier,
            quantity: coinsurancePiece.benefitQualifier.quantity,
            quantity_remaining: +limitation.benefitQualifier.quantity,
            qualifier_amount:
              coinsurancePiece.benefitQualifier.qualifier_amount,
            qualifier_amount_remaining:
              limitation.benefitQualifier.qualifier_amount,
          },
        });
        hasMerged = true;
        coinsurancePiece.matched = true;
      }
    }
    for (const cycledLimitation of limitationPieces) {
      if (
        cycledLimitation.benefitQualifier &&
        cycledLimitation.benefitQualifier.quantity_qualifier &&
        quantityQualToCompare.includes(
          cycledLimitation.benefitQualifier.quantity_qualifier,
        ) &&
        cycledLimitation.benefitQualifier.quantity &&
        cycledLimitation.benefitQualifier.time_period &&
        matchedTimePeriods.includes(
          cycledLimitation.benefitQualifier.time_period,
        ) &&
        cycledLimitation.service &&
        limitation.service == cycledLimitation.service &&
        limitation.network == cycledLimitation.network &&
        limitation.note == cycledLimitation.note
      ) {
        if (
          limitation.benefitQualifier.time_period == 'remaining' ||
          limitation.benefitQualifier.time_period == 'lifetime_remaining'
        ) {
          mergedAndParsedLimitationPieces.push({
            ...cycledLimitation,
            benefitQualifier: {
              time_period: cycledLimitation.benefitQualifier.time_period,
              quantity_qualifier:
                cycledLimitation.benefitQualifier.quantity_qualifier,
              quantity: cycledLimitation.benefitQualifier.quantity,
              quantity_remaining: +limitation.benefitQualifier.quantity,
              qualifier_amount:
                cycledLimitation.benefitQualifier.qualifier_amount,
              qualifier_amount_remaining:
                limitation.benefitQualifier.qualifier_amount,
            },
          });
          hasMerged = true;
        } else {
          mergedAndParsedLimitationPieces.push({
            ...limitation,
            benefitQualifier: {
              time_period: limitation.benefitQualifier.time_period,
              quantity_qualifier:
                limitation.benefitQualifier.quantity_qualifier,
              quantity: limitation.benefitQualifier.quantity,
              quantity_remaining: +cycledLimitation.benefitQualifier.quantity,
              qualifier_amount: limitation.benefitQualifier.qualifier_amount,
              qualifier_amount_remaining:
                cycledLimitation.benefitQualifier.qualifier_amount,
            },
          });
          hasMerged = true;
        }
      }
    }
    if (
      !hasMerged &&
      limitation.benefitQualifier &&
      limitation.benefitQualifier.quantity_qualifier &&
      limitation.benefitQualifier.quantity_qualifier != 'none' &&
      limitation.benefitQualifier.time_period != 'amount'
    ) {
      mergedAndParsedLimitationPieces.push({
        ...limitation,
      });
    } else if (!hasMerged && limitation.note) {
      otherLimitationPieces.push(limitation);
    }
  }

  for (const coinsurancePiece of coinsurancePieces) {
    if (
      onlyShowNumberOfXLimitations &&
      coinsurancePiece.benefitQualifier &&
      coinsurancePiece.benefitQualifier.quantity_qualifier != 'visits' &&
      coinsurancePiece.benefitQualifier.quantity_qualifier != 'days'
    ) {
      continue;
    }
    if (
      !coinsurancePiece.matched &&
      coinsurancePiece.benefitQualifier &&
      coinsurancePiece.benefitQualifier.quantity_qualifier &&
      coinsurancePiece.benefitQualifier.quantity_qualifier != 'none' &&
      coinsurancePiece.benefitQualifier.time_period != 'amount'
    ) {
      mergedAndParsedLimitationPieces.push({
        ...coinsurancePiece,
      });
    }
  }

  // Finally, group the coinsurance pieces and create a Benefit Record out of the groups.
  const limitationGroups = groupBenefitPieces(
    mergedAndParsedLimitationPieces,
    false,
  );

  const readableLimitations = limitationGroups.map(pieces =>
    combinePiecesIntoBenefitRecord(pieces, false),
  );

  const otherLimitationGroups = groupBenefitPieces(
    otherLimitationPieces,
    false,
  );

  const otherLimitations = otherLimitationGroups
    .map(pieces => combinePiecesIntoBenefitRecord(pieces, false))
    .map(limit => recordToEligibilityBenefit(limit))
    .map(limit => limitationToBenefitLimitation(limit));

  (benefitDescriptions ?? []).forEach(bd => {
    if (!selectedServiceType || selectedServiceType === bd.serviceTypeCode) {
      otherLimitations.push({
        custom: false,
        eligibilityBenefit: {
          comment: bd.messages.join(', '),
          level: null,
          network: null,
          service: bd.serviceTypeCode,
          placeOfService: PlaceOfServiceCode.NO_PLACE_OF_SERVICE,
          insuranceType: null,
        },
      });
    }
  });

  return {
    readableLimitations: readableLimitations,
    otherLimitations: otherLimitations,
  };
}

function limitationToBenefitLimitation(
  limitation: EligibilityBenefit,
): EstimationBenefit.Limitation {
  return {
    eligibilityBenefit: limitation,
    custom: false,
  };
}

export interface ProviderTierException {
  id: number;
  npiIds: string[];
  payerId: string;
  exceptionType: ProviderTierExceptionType | null;
  benefitType: BenefitType | null;
  insuranceProductIds: string[];
}

export enum ProviderTierExceptionType {
  TIER_2 = 'TIER_2',
  OUT_OF_NETWORK = 'OUT_OF_NETWORK',
}

export const ProviderTierExceptions: Map<
  ProviderTierExceptionType,
  { name: string }
> = new Map([
  [ProviderTierExceptionType.TIER_2, { name: 'Tier 2' }],
  [ProviderTierExceptionType.OUT_OF_NETWORK, { name: 'Out-of-network' }],
]);

export const ProviderBenefitExceptions: Map<BenefitType, { name: string }> =
  new Map([
    ['primary', { name: 'PCP' }],
    ['specialist', { name: 'Specialist' }],
  ]);

export function isUniqueProviderTierException(
  exception: ProviderTierException,
  existingExceptions: ProviderTierException[],
) {
  return !existingExceptions.some(e =>
    providerTierExceptionsOverlap(e, exception),
  );
}

export function providerTierExceptionsOverlap(
  exception1: ProviderTierException,
  exception2: ProviderTierException,
) {
  return (
    exception1.id !== exception2.id &&
    exception1.payerId === exception2.payerId &&
    exception1.npiIds.some(n => exception2.npiIds.includes(n)) &&
    (exception1.insuranceProductIds.length === 0 ||
      exception2.insuranceProductIds.length === 0 ||
      exception1.insuranceProductIds.some(ip =>
        exception2.insuranceProductIds.includes(ip),
      ))
  );
}

export function findProviderTierException(
  exceptions: ProviderTierException[],
  payerId: string | number | undefined | null,
  npiId: string | number | undefined | null,
  insuranceProductId: string | number | undefined | null,
): ProviderTierException | undefined {
  if (payerId == null || npiId == null) {
    return undefined;
  }
  return exceptions.find(e =>
    providerTierExceptionMatches(
      e,
      String(payerId),
      String(npiId),
      insuranceProductId == null ? null : String(insuranceProductId),
    ),
  );
}

export function providerTierExceptionMatches(
  exception: ProviderTierException,
  payerId: string,
  npiId: string,
  insuranceProductId: string | null,
): boolean {
  return (
    exception.payerId === payerId.toString() &&
    exception.npiIds.includes(npiId.toString()) &&
    (exception.insuranceProductIds.length === 0 ||
      (insuranceProductId !== null &&
        exception.insuranceProductIds.includes(String(insuranceProductId))))
  );
}

export interface ProviderTierExceptionSummary {
  benefitType: BenefitType | undefined;
  network: NetworkStatus;
  tier: ProviderTierExceptionType | undefined;
}

export function getProviderTierExceptionSummary(
  exceptions: ProviderTierException[],
  payerId: string | number | undefined | null,
  npiId: string | number | undefined | null,
  insuranceProductId: string | number | undefined | null,
): ProviderTierExceptionSummary {
  const exception = findProviderTierException(
    exceptions,
    payerId,
    npiId,
    insuranceProductId,
  );
  return {
    benefitType: exception?.benefitType ?? undefined,
    network:
      exception?.exceptionType === ProviderTierExceptionType.OUT_OF_NETWORK
        ? 'out'
        : 'in',
    tier: exception?.exceptionType ?? undefined,
  };
}

// Checks whether two benefits match, ignoring some fields as appropriate.
export function compareEstimationBenefits(
  benefitA: EstimationBenefit.AnyBenefit,
  benefitB: EstimationBenefit.AnyBenefit,
  excludePlaceOfService: boolean = false,
  matchForAnalytics: boolean = false,
): boolean {
  if (
    benefitA?.eligibilityBenefit?.network === 'not_applicable' ||
    benefitB?.eligibilityBenefit?.network === 'not_applicable' ||
    benefitA?.eligibilityBenefit?.network === 'unknown' ||
    benefitB?.eligibilityBenefit?.network === 'unknown'
  ) {
    // Compare ignoring network
    return lodashIsEqual(
      {
        ...benefitA,
        eligibilityBenefit: {
          ...benefitA?.eligibilityBenefit,
          network: undefined,
        },
        cascadingBenefit: undefined,
        cumulativeAmount: undefined,
      },
      {
        ...benefitB,
        eligibilityBenefit: {
          ...benefitB?.eligibilityBenefit,
          network: undefined,
        },
        cascadingBenefit: undefined,
        cumulativeAmount: undefined,
      },
    );
  }
  if (excludePlaceOfService) {
    return lodashIsEqual(
      {
        ...benefitA,
        eligibilityBenefit: {
          ...benefitA?.eligibilityBenefit,
          network: undefined,
          placeOfService: null,
        },
        cascadingBenefit: undefined,
        cumulativeAmount: undefined,
      },
      {
        ...benefitB,
        eligibilityBenefit: {
          ...benefitB?.eligibilityBenefit,
          network: undefined,
          placeOfService: null,
        },
        cascadingBenefit: undefined,
        cumulativeAmount: undefined,
      },
    );
  }
  if (matchForAnalytics) {
    if (
      benefitA.eligibilityBenefit == null &&
      benefitB.eligibilityBenefit == null &&
      benefitA.custom == false &&
      benefitB.custom == false
    ) {
      // Non-custom benefits without an eligibility benefit are defaults.
      return true;
    }
    // Whether a benefit is cascading isn't relevant to the analytics matching
    return lodashIsEqual(
      {
        ...benefitA,
        cascadingBenefit: undefined,
        cumulativeAmount: undefined,
      },
      {
        ...benefitB,
        cascadingBenefit: undefined,
        cumulativeAmount: undefined,
      },
    );
  }
  return lodashIsEqual(benefitA, benefitB);
}

/**
 * sets deductible/oop cacadingBenefit property to true if the benefit
 * matches the previous benefit of the same payer
 */
export function setCascading(
  treatments: EstimationTreatment[],
): EstimationTreatment[] {
  const prevPayerTreatmentDedOOP = new KeyedMap<
    PatientPayerId,
    CumulativeBenefits
  >(PatientPayerId.KEY);

  return treatments.map(treatment => {
    const payerIds = patientPayerIds(
      treatment.payers.map(payer => payer.payerId),
    );

    return {
      ...treatment,
      payers: treatment.payers.map((payer, payerIndex) => {
        const payerId = payerIds[payerIndex];
        const prevDedOop = prevPayerTreatmentDedOOP.get(payerId);
        prevPayerTreatmentDedOOP.set(payerId, {
          deductible: payer.benefit.deductible,
          oop: payer.benefit.oop,
        });

        if (!prevDedOop) {
          return payer;
        }

        return {
          ...payer,
          benefit: {
            ...payer.benefit,
            deductible: {
              ...payer.benefit.deductible,
              cascadingBenefit: shouldCascade(
                payer.benefit.deductible,
                prevDedOop.deductible,
              )
                ? true
                : payer.benefit.deductible.cascadingBenefit,
            },
            oop: {
              ...payer.benefit.oop,
              cascadingBenefit: shouldCascade(payer.benefit.oop, prevDedOop.oop)
                ? true
                : payer.benefit.oop.cascadingBenefit,
            },
          },
        };
      }),
    };
  });
}

/**
 * returns true if the benefit is labeled as a cascading benefit OR if
 * they are the same benefit as the previous benefit (excluding cascadingBenefit property)
 */
function shouldCascade(
  currentBenefit: EstimationBenefit.Deductible | EstimationBenefit.Oop,
  previousBenefit: EstimationBenefit.Deductible | EstimationBenefit.Oop,
) {
  return (
    currentBenefit.cascadingBenefit ||
    isEqual(
      { ...currentBenefit, cascadingBenefit: true },
      {
        ...previousBenefit,
        cascadingBenefit: true,
      },
    )
  );
}
